/**
 * OverviewPageV2 — Phase 1 of the Rebirth migration.
 *
 * Implements the outcomes-first Overview specified in
 * Agentlets-Design-Principles.md §6.1:
 *   1. Posture banner (the trust-narrative moment)
 *   2. Four hero outcomes (Hours saved · ROI · Throughput · Risk avoided)
 *   3. Top priorities (toxic combinations + approvals + expiring certs)
 *   4. Fleet preview table
 *   5. Recent governance activity feed
 *
 * Built entirely from v2 primitives. Wraps the v1 OverviewPage when the
 * rebirth.overview.v2 flag is OFF (composition rule §5.3).
 *
 * BACKEND NOTES:
 * Until the dedicated /api/overview/business-outcomes endpoint lands
 * (tracked in the Implementation Plan §4.1), the four hero metrics are
 * derived from existing PlatformContext fields where possible and
 * fall back to representative defaults so the page is always renderable.
 * Replace each `metricsFallback.*` value with the real backend response
 * the moment that endpoint ships.
 */
import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Bot, AlertTriangle, FileText, Clock, CheckCircle2, Zap, DollarSign, ShieldAlert, RefreshCw, Download } from 'lucide-react';
import { usePlatform } from '../contexts/PlatformContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { isRebirthOverviewEnabled } from '../utils/featureFlags';
import OverviewPage from './OverviewPage';
import {
  PostureBanner, MetricCard, Card, Pill, Button, TrustChip,
  Table, GovernanceJourney,
} from '../components/v2';
import NewAgentletMenu from '../components/v2/NewAgentletMenu';
import { agentCreateRoute } from '../data/agentArchetypes';

/* ────────────────────────────────────────────────────────────────
   Demo data — kept as placeholder copy for the hero metrics +
   priorities + activity sections. The /api/v1/overview/business-
   outcomes endpoint hasn't shipped yet so these widgets show the
   design fixture with an explicit "[Demo]" badge until they do.
   The Fleet preview was REMOVED from this constant after G415 —
   that widget now binds to the real, tenant-scoped `agents` list.
   ──────────────────────────────────────────────────────────────── */

/**
 * G415 — Map a real Agent row (from PlatformContext) into the shape
 * the fleet preview table expects. Replaces the hardcoded FLEET_PREVIEW
 * fixture so the fleet table is scope-correct: a Beta Viewer sees only
 * Beta agents, not the Acme demo set.
 *
 * Some columns (trust score, throughput) require backend signals that
 * aren't yet on the Agent row. For those we show `—` rather than a
 * fake number — the operator deserves to know what's measured and
 * what isn't.
 */
function toFleetRow(agent) {
  if (!agent || !agent.id) return null;
  // Archetype mapping for the Type pill. Falls back to Native when
  // unknown (Let / AutoLet / SysLet → Native; ExteLet → External).
  const archetype = agent.archetype || agent.type || 'Native';
  const typeLabel = archetype === 'ExteLet' || archetype === 'External' ? 'External' : 'Native';
  // Phase shows the lifecycle phase the operator cares about.
  const phaseRaw = (agent.phase || agent.lifecyclePhase || '').toString();
  const phase = phaseRaw ? phaseRaw.charAt(0).toUpperCase() + phaseRaw.slice(1).toLowerCase() : 'Build';
  // 2-letter monogram from name. Avoids the fake "iconTone" mapping
  // that was hardcoded per-fixture-row.
  const words = (agent.name || '?').split(/\s+/).filter(Boolean);
  const iconLabel = (words[0]?.[0] || '?').toUpperCase()
                  + (words[1]?.[0] || words[0]?.[1] || '').toUpperCase();
  return {
    id: agent.id,
    name: agent.name || '(unnamed)',
    type: typeLabel,
    category: agent.category || agent.classification || '—',
    phase,
    // Canonical trust = complianceScore (null = Unrated); never the trustScore
    // baseline (75/50). TrustChip renders "Unrated" for null.
    // See docs/design/TRUST_SCORE_SINGLE_SOURCE_RC.md.
    trust: typeof agent.complianceScore === 'number' ? agent.complianceScore : null,
    autonomy: (agent.autonomy || 'SUPERVISED').toString().toUpperCase(),
    last: agent.lastActivityAt
        ? new Date(agent.lastActivityAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : '—',
    tput: typeof agent.throughputPerDay === 'number' ? agent.throughputPerDay.toLocaleString() : '—',
    iconLabel,
    iconTone: typeLabel === 'External' ? 'trust' : 'cyan',
  };
}

const ACTIVITY = [
  { time: '14:08:22', tone: 'success', text: 'Sophie Cognito · grant_temp_access approved by m.chen@acme.io. SLA 4m 22s.', hash: '#4d8f1a' },
  { time: '14:01:11', tone: 'danger',  text: 'Aria Threat · Layer 2 (DPG) BLOCK on PHI exfiltration. Auto-demoted to SUPERVISED.', hash: '#4d8f19' },
  { time: '13:52:04', tone: 'trust',   text: 'Echo Compliance · SOC 2 CC6.1 evidence pack auto-generated for B-2026-05-08-A.', hash: '#4d8f18' },
  { time: '13:44:38', tone: 'warning', text: 'Vela AppSec · false-positive rate climbed to 11.2%. Trust 84 → 81.', hash: '#4d8f17' },
  { time: '13:31:09', tone: 'cyan',    text: 'Nova IR advanced DECIDE → ACT. 14 of 14 phase gates passed.', hash: '#4d8f16' },
  { time: '13:22:51', tone: 'success', text: 'Hash-chain verification window passed. 184,920 events. 0 breaks.', hash: '#4d8f15' },
];

const PRIORITIES = [
  {
    tone: 'danger', icon: AlertTriangle,
    title: 'Toxic combination · Aria Threat (External)',
    meta: 'elevated autonomy + PII flow + violation 12h ago',
    body: <>Self-autonomous + PHI/PII edge + 1 BLOCK in last 24h. Trust score dropped to 76. Auto-demotion in <strong>3h 12m</strong> if unresolved.</>,
    cta: 'Review',
    href: '/agents',
  },
  {
    tone: 'warning', icon: Clock,
    title: '7 approvals pending',
    meta: '2 past SLA · oldest 2h 14m',
    body: <>Sophie Cognito queued <strong>3 access grants</strong>. Echo Compliance queued <strong>2 audit exports</strong>. Vela AppSec queued <strong>2 patches</strong>.</>,
    cta: 'Open queue',
    href: '/approvals',
  },
  {
    tone: 'trust', icon: FileText,
    title: '2 compliance certs expiring',
    meta: 'SOC 2 quarterly · within 14 days',
    body: <>Q2-2026 SOC 2 CC6.1 certificate expires <strong>May 22</strong>. Re-evidence requires re-running 3 workflow batches. Auto-renewal eligible.</>,
    cta: 'Auto-renew',
    href: '/compliance',
  },
];

/* ────────────────────────────────────────────────────────────────
   Page
   ──────────────────────────────────────────────────────────────── */

function OverviewPageV2Inner() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const auth = useAuth();
  const orgId = auth?.session?.orgId;
  const tenantId = auth?.session?.tenantId;
  // Same agent.create gate the create affordances everywhere else use (NewAgentletMenu / inventory).
  const canCreateAgent = !!auth?.hasPermission && auth.hasPermission('agent.create');
  const { agents, loadAgents, loadDashboardMetrics } = usePlatform();
  const [refreshing, setRefreshing] = React.useState(false);

  // DEF-EXEC-003 — Auto-load fleet + dashboard metrics on mount.
  // PlatformContext does not auto-fetch agents on provider mount, so a
  // user who lands directly on Overview (default route, deep-link, browser
  // refresh) sees `agents=[]` and the "No Agentlets yet" empty-state copy
  // even when the tenant actually has many agents. Once they navigated to
  // /agents, the inventory page's own mount-load filled the cache and a
  // return to Overview would render correctly — confusing.
  //
  // The inventory page (AgentsInventoryPageV2) does the same fetch on
  // mount; we mirror that here so Overview is self-sufficient. Deps cover
  // the scope-switch case (tenant/org switcher) so a context change
  // re-fetches the right tenant's agents.
  // Deps intentionally omit loadAgents / loadDashboardMetrics: context
  // setters change identity on every PlatformContext re-render, which
  // would re-fire the fetch on unrelated state churn (e.g. a toast
  // appearing). Keying on [orgId, tenantId] re-fetches when the scope
  // genuinely changes and is stable for the rest of the lifecycle.
  useEffect(() => {
    if (!loadAgents) return;
    Promise.all([
      loadDashboardMetrics?.({ orgId, tenantId }),
      loadAgents?.({ orgId, tenantId }),
    ]).catch(() => { /* swallow — context already surfaces errors */ });
  }, [orgId, tenantId]);

  // Until /api/overview/business-outcomes ships, derive headline counts
  // from PlatformContext where we can; fall back to representative defaults.
  const metricsFallback = useMemo(() => ({
    hoursSaved:  { value: '12,847', unit: 'hrs',  delta: '18.4%', trend: [22,20,18,15,16,12,14,9,8,10,6,5,3,2] },
    roi:         { value: '$3.24',  unit: 'M',    delta: '11-mo payback', trend: [26,25,22,21,18,16,12,11,8,6,4] },
    throughput:  { value: '184,920', unit: '/wk', delta: '98.7% success', trend: [18,14,20,12,16,10,13,8,11,6,9,4] },
    riskAvoided: { value: '1,243',  unit: '',     delta: 'interventions', trend: [18,16,17,12,15,10,11,7,9,5,4] },
  }), []);

  // G415 — Real, tenant-scoped fleet. The agents array comes from
  // PlatformContext which fetches /api/v1/agents — that endpoint enforces
  // org+tenant scope via the RbacService row-scope helpers (G398). So a
  // Beta Viewer sees only Beta agents; an empty tenant shows []. Sliced
  // to the first 6 rows for the preview widget; the "Open inventory"
  // link sends the operator to the full list.
  const realFleet = useMemo(
    () => (Array.isArray(agents) ? agents : []).map(toFleetRow).filter(Boolean),
    [agents]
  );
  const fleetCount = realFleet.length;
  const fleetPreview = realFleet.slice(0, 6);

  /* G188 — surgical bug fixes: wire the four previously-dead header buttons
   * + fleet row click. The page's data sources, hero metrics, copy, layout,
   * priorities, and activity feed are intentionally preserved as designed
   * per Agentlets-Design-Principles.md §6.1 — the V2 backend endpoint
   * `/api/overview/business-outcomes` will swap the fallback values when
   * it ships. Until then we only wire the action handlers so buttons stop
   * being dead. */
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        loadDashboardMetrics?.({ orgId, tenantId }),
        loadAgents?.({ orgId, tenantId }),
      ]);
      addToast({ tone: 'success', title: 'Overview refreshed' });
    } catch (e) {
      addToast({ tone: 'danger', title: 'Refresh failed', description: e?.message || String(e) });
    } finally {
      setRefreshing(false);
    }
  };

  const handleExportSnapshot = () => {
    try {
      const snapshot = {
        exportedAt: new Date().toISOString(),
        scope: { orgId, tenantId },
        outcomes: { _demo: true, ...metricsFallback },
        fleetPreview, // G415 — real scoped data, no longer the fixture
        priorities: { _demo: true, items: PRIORITIES.map(p => ({ tone: p.tone, title: p.title, meta: p.meta, href: p.href })) },
        activity: { _demo: true, items: ACTIVITY },
      };
      const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `overview-snapshot-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 0);
      addToast({ tone: 'success', title: 'Snapshot exported' });
    } catch (e) {
      addToast({ tone: 'danger', title: 'Export failed', description: e?.message || String(e) });
    }
  };

  /* Fleet drill-down hop. v1 expects the selected agent id in sessionStorage
   * so AgentsPage opens the right detail panel on landing. */
  const handleFleetRowClick = (row) => {
    try {
      if (row?.id) sessionStorage.setItem('agentlets_nav_selectedAgent', row.id);
    } catch {}
    navigate('/agents');
  };

  return (
    <div className="min-h-full bg-v2-page text-v2-ink p-4 sm:p-7 max-w-[1400px] mx-auto">

      {/* ── Page header ─────────────────────────────────────── */}
      <header className="flex items-start gap-4 mb-6">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight m-0">Outcomes</h1>
          <p className="text-[13px] text-v2-ink-secondary mt-0.5">
            24h ending now &middot; last refresh just now
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="ghost"
            size="md"
            iconLeft={<RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />}
            onClick={handleRefresh}
            loading={refreshing}
          >
            Refresh
          </Button>
          <Button
            variant="secondary"
            size="md"
            iconLeft={<Download size={13} />}
            onClick={handleExportSnapshot}
          >
            Export Snapshot
          </Button>
          {/* Single shared New-Agentlet menu — same taxonomy + RBAC gating as
            * every other entry point. Off-page, so each pick navigates to the
            * one canonical create surface. */}
          <NewAgentletMenu onPick={(key) => navigate(agentCreateRoute(key))} />
        </div>
      </header>

      {/* ── Posture banner ─────────────────────────────────────
       * G415 — Posture data (trust score, toxic combinations, hash-chain
       * count) requires the /api/v1/overview/business-outcomes endpoint
       * that hasn't shipped. Until then, show the banner with a clear
       * "Demo data" badge so operators know these numbers are placeholders
       * and not their actual tenant metrics. The fleet count IS now real
       * (scoped via /api/v1/agents) so we use that. */}
      <div className="mb-6">
        <PostureBanner
          score={fleetCount === 0 ? null : 87}
          title={fleetCount === 0
            ? (canCreateAgent
                ? 'No Agentlets yet — create your first to start governing'
                : 'No Agentlets yet in this workspace')
            : `Fleet posture: Strong — ${Math.min(fleetCount, 38)} of ${fleetCount} Agentlets governed`}
          message={fleetCount === 0
            ? (canCreateAgent
                ? <>Once you create or onboard your first Agentlet for this tenant, this banner will show your real posture.</>
                : <>Once an administrator creates or onboards the first Agentlet for this tenant, this banner will show your real posture.</>)
            : <>
                <Pill tone="muted" size="sm">Demo data</Pill>{' '}
                Trust score average <strong className="text-v2-ink">87.3</strong>, up 2.1 vs last week.{' '}
                <strong className="text-v2-brand">3 toxic combinations</strong> require review. Hash-chain verification: 100% intact across 184k events.
              </>}
          action={fleetCount === 0
            ? (canCreateAgent ? <Button variant="primary" onClick={() => navigate('/agents')}>Create Agentlet</Button> : null)
            : <Button variant="primary" onClick={() => navigate('/protection-hub')}>Review priorities</Button>}
        />
      </div>

      {/* ── Hero outcomes ─────────────────────────────────────
       * G415 — These four metrics are placeholders pending the
       * /api/v1/overview/business-outcomes endpoint. The Pill makes the
       * status explicit so a CISO reviewing the page doesn't mistake the
       * numbers for real reporting. */}
      <section aria-label="Business outcomes" className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Pill tone="muted" size="sm">Demo data</Pill>
          <span className="text-[11px] text-v2-ink-muted">
            Awaiting <code className="font-mono-v2">/api/v1/overview/business-outcomes</code>
          </span>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Hours of human work saved"
          value={metricsFallback.hoursSaved.value}
          unit={metricsFallback.hoursSaved.unit}
          tone="success"
          icon={<Clock size={11} />}
          delta={{ value: metricsFallback.hoursSaved.delta, direction: 'up', baseline: 'vs. previous 30 days', intent: 'positive' }}
          trend={metricsFallback.hoursSaved.trend}
        />
        <MetricCard
          label="Realized ROI"
          value={metricsFallback.roi.value}
          unit={metricsFallback.roi.unit}
          tone="trust"
          icon={<DollarSign size={11} />}
          delta={{ value: metricsFallback.roi.delta, direction: 'up', baseline: '387% 3-yr proj.', intent: 'positive' }}
          trend={metricsFallback.roi.trend}
        />
        <MetricCard
          label="Protected throughput"
          value={metricsFallback.throughput.value}
          unit={metricsFallback.throughput.unit}
          tone="cyan"
          icon={<Zap size={11} />}
          delta={{ value: metricsFallback.throughput.delta, direction: 'up', baseline: 'p95 940ms', intent: 'positive' }}
          trend={metricsFallback.throughput.trend}
          trendVariant="bar"
        />
        <MetricCard
          label="Risk avoided"
          value={metricsFallback.riskAvoided.value}
          unit={metricsFallback.riskAvoided.unit}
          tone="brand"
          icon={<ShieldAlert size={11} />}
          delta={{ value: metricsFallback.riskAvoided.delta, direction: 'up', baseline: '0 escapes · MTTR −71%', intent: 'positive' }}
          trend={metricsFallback.riskAvoided.trend}
        />
        </div>
      </section>

      {/* ── Governance journey ─────────────────────────────────
       * Prove7's seven-stage per-agent journey, mapped 1:1 to real
       * platform components (PromotionController, RbacService,
       * IntentBindingService, PolicyEnforcementGate, hash-chain).
       * The Trust gate at step 5 (Build · Decide · Act) sits AFTER
       * RBAC because PromotionController's APPROVED → PROMOTED
       * transition requires role bindings to already be resolvable
       * — runtime enforcement post-promotion is scope-checked.
       * Counts derive client-side from the same /api/v1/agents
       * fleet used elsewhere on this page until a dedicated
       * /api/v1/overview/governance-journey rollup endpoint ships. */}
      <GovernanceJourney agents={agents} />

      {/* ── Top priorities ─────────────────────────────────────
       * G415 — Same status as the hero metrics: hardcoded fixture
       * cards pending the priorities backend endpoint. Badge so the
       * operator knows these aren't real findings for their tenant. */}
      <section className="mb-6">
        <header className="flex items-center gap-3 mb-3">
          <h2 className="text-[14.5px] font-bold tracking-tight m-0">Top priorities</h2>
          <Pill tone="muted" size="sm">Demo data</Pill>
          <span className="text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted">
            3 toxic combinations · 7 approvals · 2 expiring certs
          </span>
          <button type="button" onClick={() => navigate('/protection-hub')} className="ml-auto text-[12px] text-v2-action font-medium hover:text-v2-action-strong">
            View all →
          </button>
        </header>
        <div className="grid lg:grid-cols-3 gap-4">
          {PRIORITIES.map((p, i) => {
            const Icon = p.icon;
            const ICON_TONE = {
              danger:  'bg-v2-danger-soft text-v2-danger',
              warning: 'bg-v2-warning-soft text-v2-warning',
              trust:   'bg-v2-trust-soft text-v2-trust',
            };
            return (
              <Card key={i} interactive onClick={() => navigate(p.href)} elevation="sm">
                <div className="flex items-start gap-3 mb-2">
                  <div aria-hidden="true" className={'w-7 h-7 rounded-v2-sm flex items-center justify-center flex-shrink-0 ' + (ICON_TONE[p.tone] || ICON_TONE.warning)}>
                    <Icon size={14} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[14px] font-semibold leading-tight text-v2-ink">{p.title}</div>
                    <div className="text-[11.5px] text-v2-ink-muted mt-0.5">{p.meta}</div>
                  </div>
                </div>
                <p className="text-[12.5px] text-v2-ink-secondary leading-relaxed mb-2">{p.body}</p>
                <div className="text-[12px] font-semibold text-v2-action">{p.cta} →</div>
              </Card>
            );
          })}
        </div>
      </section>

      {/* ── Fleet preview ─────────────────────────────────────
       * G415 — Real, tenant-scoped fleet. Empty state when the caller's
       * tenant has zero agents (e.g. a freshly-onboarded Beta Viewer).
       * The row count reflects the actual scoped list, not a hardcoded
       * 42, so the "X of Y by recent activity" subhead is honest. */}
      <section className="mb-6">
        <header className="flex items-center gap-3 mb-3">
          <h2 className="text-[14.5px] font-bold tracking-tight m-0">Agentlet fleet</h2>
          <span className="text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted">
            {fleetCount === 0
              ? 'No Agentlets in scope'
              : `${fleetPreview.length} of ${fleetCount} by recent activity`}
          </span>
          <button type="button" onClick={() => navigate('/agents')} className="ml-auto text-[12px] text-v2-action font-medium hover:text-v2-action-strong">
            Open inventory →
          </button>
        </header>
        {fleetCount === 0 ? (
          <Card padding="lg" elevation="sm">
            <div className="text-center py-8">
              <Bot size={32} className="text-v2-ink-muted mx-auto mb-3" aria-hidden="true" />
              <div className="text-[14px] font-semibold text-v2-ink mb-1">No Agentlets in this tenant yet</div>
              <div className="text-[12px] text-v2-ink-muted mb-4">
                Create your first Agentlet to start protecting it. Your scope is determined by your active membership in the user menu.
              </div>
              <Button variant="primary" iconLeft={<Plus size={13} />} onClick={() => navigate('/agents')}>
                Create your first Agentlet
              </Button>
            </div>
          </Card>
        ) : (
          <Table
            ariaLabel="Agentlet fleet preview"
            columns={[
              { key: 'name', header: 'Agentlet', render: (r) => (
                <div className="flex items-center gap-3">
                  <div className={'w-7 h-7 rounded-v2-sm flex items-center justify-center font-bold text-[10px] ' + (
                    r.iconTone === 'cyan' ? 'bg-v2-cyan-soft text-v2-cyan' :
                    r.iconTone === 'brand' ? 'bg-v2-brand-soft text-v2-brand-strong' :
                    r.iconTone === 'success' ? 'bg-v2-success-soft text-v2-success' :
                    r.iconTone === 'warning' ? 'bg-v2-warning-soft text-v2-warning' :
                    r.iconTone === 'trust' ? 'bg-v2-trust-soft text-v2-trust' :
                    'bg-v2-muted text-v2-ink-secondary'
                  )}>{r.iconLabel}</div>
                  <div>
                    <div className="font-semibold text-v2-ink">{r.name}</div>
                    <div className="text-[10.5px] text-v2-ink-muted">{r.category}</div>
                  </div>
                </div>
              )},
              { key: 'type',  header: 'Type',     render: (r) => <Pill tone={r.type === 'External' ? 'trust' : 'cyan'} size="sm">{r.type}</Pill> },
              { key: 'phase', header: 'Phase',    render: (r) => <Pill tone="cyan" size="sm">{r.phase}</Pill> },
              { key: 'trust', header: 'Trust',    render: (r) => r.trust == null ? <span className="text-v2-ink-muted">—</span> : <TrustChip score={r.trust} autonomy={r.autonomy} size="sm" /> },
              { key: 'last',  header: 'Last',     align: 'right', render: (r) => <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{r.last}</span> },
              { key: 'tput',  header: 'Tput / d', align: 'right', render: (r) => <span className="font-semibold tabular-nums">{r.tput}</span> },
            ]}
            rows={fleetPreview}
            rowKey={(r) => r.id}
            onRowClick={handleFleetRowClick}
          />
        )}
      </section>

      {/* ── Recent activity ───────────────────────────────────
       * G415 — Activity feed is hardcoded fixture pending the
       * tenant-scoped governance-activity endpoint. Operators
       * needing real audit events should click through to the
       * Audit Trail page (which is real and scoped). */}
      <section className="mb-2">
        <header className="flex items-center gap-3 mb-3">
          <h2 className="text-[14.5px] font-bold tracking-tight m-0">Recent protection activity</h2>
          <Pill tone="muted" size="sm">Demo data</Pill>
          <span className="text-[10.5px] uppercase tracking-wider font-semibold text-v2-ink-muted">
            Hash-chained · last 6 events shown
          </span>
          <button type="button" onClick={() => navigate('/workflow-audit')} className="ml-auto text-[12px] text-v2-action font-medium hover:text-v2-action-strong">
            Audit Trail →
          </button>
        </header>
        <Card padding="none" elevation="sm">
          {ACTIVITY.map((row, i) => {
            const TONE = {
              success: { bg: 'bg-v2-success-soft', text: 'text-v2-success', glyph: <CheckCircle2 size={12} /> },
              danger:  { bg: 'bg-v2-danger-soft',  text: 'text-v2-danger',  glyph: <AlertTriangle size={12} /> },
              warning: { bg: 'bg-v2-warning-soft', text: 'text-v2-warning', glyph: <AlertTriangle size={12} /> },
              trust:   { bg: 'bg-v2-trust-soft',   text: 'text-v2-trust',   glyph: <FileText size={12} /> },
              cyan:    { bg: 'bg-v2-cyan-soft',    text: 'text-v2-cyan',    glyph: <Zap size={12} /> },
            }[row.tone];
            return (
              <div key={i} className={'flex items-center gap-3.5 px-4 py-2.5 text-[12.5px] ' + (i < ACTIVITY.length - 1 ? 'border-b border-v2-border-subtle' : '') + ' hover:bg-v2-muted transition-colors duration-v2'}>
                <span className="text-[11px] font-mono-v2 text-v2-ink-muted min-w-[60px]">{row.time}</span>
                <span aria-hidden="true" className={'w-5 h-5 rounded-v2-sm flex items-center justify-center flex-shrink-0 ' + TONE.bg + ' ' + TONE.text}>{TONE.glyph}</span>
                <span className="flex-1 text-v2-ink-secondary truncate">{row.text}</span>
                <Pill tone="success" size="sm" dot>{row.hash}</Pill>
              </div>
            );
          })}
        </Card>
      </section>
    </div>
  );
}

/**
 * Default export resolves v1 vs v2 at render time. App.jsx imports
 * OverviewPage; this swaps in the v2 page when the flag is on.
 *
 * Wait — App.jsx already imports OverviewPage. So we don't change
 * App.jsx; instead we edit OverviewPage to delegate. See the wrapper
 * file structure: this module is only rendered via the wrapper.
 */
export default function ResolvedOverviewPage(props) {
  if (isRebirthOverviewEnabled()) return <OverviewPageV2Inner {...props} />;
  return <OverviewPage {...props} />;
}

export { OverviewPageV2Inner };
